import { ReactNode } from 'react';

import { useImageLoad } from '@backrs_ui/common-ui/src/hooks/useImageLoad';

import { Box, Typography, SxProps, Theme, Button } from '@mui/material';

const FIGMA_BUTTON_WIDTH = 212;

type HeaderCTACardProps = Readonly<{
  id?: string;
  title: string;
  /** Both are needed for the button to appear; without them the card is just the banner. */
  buttonText?: string;
  buttonOnClick?: () => void;
  imageUrl: string;
  rightContent?: ReactNode;
}>;

export const HeaderCTACard = ({
  id,
  title,
  buttonText,
  buttonOnClick,
  imageUrl,
  rightContent,
}: HeaderCTACardProps) => {
  const image = useImageLoad(imageUrl);

  return (
    <Box sx={containerSx}>
      <Box sx={contentContainerSx}>
        <Box sx={topRowSx}>
          {rightContent}
          <Typography sx={titleSx}>{title}</Typography>
        </Box>
        {buttonText && buttonOnClick && (
          <Button
            id={id}
            hierarchy="Secondary gray"
            size="md"
            onClick={buttonOnClick}
            sx={buttonSx}
          >
            {buttonText}
          </Button>
        )}
      </Box>
      {!image.imageError && (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          sx={imageWithLoadSx(image.imageLoaded)}
          {...image.imageProps}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Foreground/fg-primary'],
  borderRadius: backrs.radius['radius-xl'],
  display: 'flex',
  flexDirection: 'column',
  containerType: 'inline-size',
  overflow: 'hidden',
  paddingBottom: backrs.spacing['spacing-2xl'],
  position: 'relative',
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  alignItems: 'flex-start',
  paddingTop: backrs.spacing['spacing-2xl'],
  position: 'relative',
  height: '280px',
  zIndex: 1,
  '@container (min-width: 550px)': {
    height: '360px',
  },
  '@container (min-width: 600px)': {
    paddingTop: backrs.spacing['spacing-3xl'],
    px: backrs.spacing['spacing-4xl'],
  },
});

const topRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: backrs.spacing['spacing-xs'],
  width: '100%',
  '@container (min-width: 480px)': {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
});

const titleSx: SxProps<Theme> = ({ typography, backrs }) => ({
  color: backrs.colors['Colors/Base/White'],
  ...typography['display-xs-semibold'],
  alignSelf: 'flex-start',
  '@container (min-width: 480px)': {
    order: -1,
  },
  '@container (min-width: 600px)': {
    ...typography['display-md-semibold'],
  },
});

const buttonSx: SxProps<Theme> = {
  width: {
    sm: `${FIGMA_BUTTON_WIDTH}px`,
  },
};

const imageWithLoadSx =
  (loaded: boolean): SxProps<Theme> =>
  () => ({
    pointerEvents: 'none',
    objectFit: 'contain',
    width: '100%',
    maxWidth: '740px',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    opacity: loaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
  });
